import React from 'react';
import './RentalListTable.style.css';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Col,
  Content,
  ModalLayout,
  Row,
  Table,
  Text,
} from 'components';
import OrderItem from '../OrderItem/OrderItem';
import { stringToMoneyFormat } from 'utils';
import Formatter from 'utils/Formatter';
import { Input, Select } from 'antd';
import { encrypt } from 'utils/Crypto';
import dayjs from 'dayjs';

const REFUND_REASON = {
  CANCELLED: [
    {
      label: '단순변심',
      value: '고객 변심으로 인한 주문 취소',
    },
    {
      label: '옵션변경',
      value: '옵션 변경 후 재구매',
    },
    {
      label: '배송지변경',
      value: '배송지 변경 후 재구매',
    },
    {
      label: '배송지연',
      value: '배송지연으로 인한 주문 취소',
    },
    {
      label: '직접입력',
      value: null,
    },
  ],
  REFUNDED: [
    {
      label: '단순변심',
      value: '고객 변심으로 인한 주문 취소',
    },
    {
      label: '상품파손',
      value: '상품이 파손됨',
    },
    {
      label: '상품오설명',
      value: '상품이 설명과 다름',
    },
    {
      label: '구성품누락',
      value: '상품의 구성품/부속품이 들어있지 않음',
    },
    {
      label: '상품결함',
      value: '상품이 결함/기능에 이상이 있음',
    },
    {
      label: '배송누락',
      value: '주문한 상품 중 일부 상품이 배송되지 않음',
    },
    {
      label: '배송문제',
      value: '다른 상품이 배송됨',
    },
    {
      label: '직접입력',
      value: null,
    },
  ],
};

const REFUND_STATUS = {
  PENDING: '환불 접수',
  APPROVED: '환불 처리중',
  REJECTED: '환불거절',
  COMPLETED: '환불완료',
};

const RentalListTable = ({
  orders,
  detailText = '주문상세',
  onSubmit,
  onDetailClick,
}) => {
  /* ===== Route ===== */
  const navigate = useNavigate();

  /* ===== Props ===== */
  /* ===== State ===== */
  const today = dayjs().unix();

  const [modalType, setModalType] = React.useState(null);
  const [modalData, setModalData] = React.useState(null);

  const [selectedValue, setSelectedValue] = React.useState('');
  const [inputValue, setInputValue] = React.useState('');

  /* ===== Hooks ===== */
  /* ===== Function ===== */
  /* ===== Variable ===== */
  /* ===== Hooks ===== */
  /* ===== Render ===== */
  return (
    <React.Fragment>
      <Table
        data={orders}
        columns={[
          {
            key: 'orderDetails',
            render: (_, state) => {
              const representativeDate = Formatter.unixToDate(
                state.created_at,
                'YYYY.MM.DD'
              );

              return (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    borderTop: '2px solid var(--black-color)',
                  }}
                >
                  <div>
                    <Text
                      style={{
                        textAlign: 'start',
                        fontSize: 16,
                        fontWeight: 'bold',
                        borderBottom: '1px solid #ddd',
                        padding: '16px 0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div>
                        <span style={{ fontWeight: 400, paddingRight: 24 }}>
                          주문번호
                        </span>
                        {state.payment_serial_number}
                      </div>
                      {detailText ? (
                        <span
                          style={{
                            fontSize: 14,
                            fontWeight: 400,
                            border: '1px solid var(--black-color)',
                            padding: '2px 8px',
                            cursor: 'pointer',
                          }}
                          onClick={() => onDetailClick(state)}
                        >
                          {detailText}
                        </span>
                      ) : null}
                    </Text>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      padding: '24px 0',
                    }}
                  >
                    {state?.rental?.map((rental, index) => (
                      <div
                        key={`product-${state?.shop_order_id}-${rental?.rental_id}`}
                        style={{
                          marginBottom:
                            index !== state?.rental?.length - 1 ? 32 : 0,
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <div style={{ width: '9.36%', textAlign: 'start' }}>
                            <Text style={{ fontSize: 16, fontWeight: 400 }}>
                              {representativeDate}
                            </Text>
                          </div>
                          <div style={{ width: '45.4%' }}>
                            <OrderItem
                              src={rental.product.thumbnail}
                              placeName={rental.product.shop_information_name}
                              productName={rental.product.product_name}
                              subName={`계약기간 ${
                                rental?.contract_date
                              }개월 / 의무사용기간 ${
                                rental?.mandatory_usage_date
                              }개월${
                                rental?.advance_price
                                  ? ` / 선납입금 ${stringToMoneyFormat(
                                      rental?.advance_price,
                                      '원'
                                    )}`
                                  : ''
                              }`}
                              productData={
                                rental.product.product_option
                                  ? rental.product.product_option
                                      .map((cur) =>
                                        cur.option
                                          .map((op) => op.option_name)
                                          .join(' / ')
                                      )
                                      .join(' / ')
                                  : null
                              }
                              onClick={() =>
                                navigate(
                                  `/products/${encodeURIComponent(
                                    encrypt(rental.product.product_id)
                                  )}`
                                )
                              }
                            />
                          </div>

                          <div style={{ width: '12.97%' }}>
                            <Text size={18} weight={700}>
                              월 {stringToMoneyFormat(rental.rental_price)}
                              <span>원</span>
                            </Text>

                            <Text size={12} color={'#666666'}>
                              결제 예정일:{' '}
                              {rental?.rental_updated_at
                                ? Formatter.unixToDate(
                                    rental?.rental_updated_at,
                                    'YYYY.MM.DD'
                                  )
                                : '-'}
                            </Text>
                          </div>

                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                            }}
                          >
                            {rental?.refund ? (
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                }}
                              >
                                <Text>
                                  {REFUND_STATUS[rental?.refund?.refund_status]}
                                </Text>
                              </div>
                            ) : (
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                }}
                              >
                                <Text
                                  style={{
                                    fontSize: 16,
                                    fontWeight: 400,
                                    textAlign: 'center',
                                  }}
                                >
                                  {Formatter.unixToDate(
                                    rental?.delivery_date,
                                    'YYYY.MM.DD'
                                  )}{' '}
                                </Text>
                                <span>
                                  {rental?.delivery_status === 'DELIVERED'
                                    ? '배송완료'
                                    : '배송예정'}
                                </span>
                                {rental?.delivery_status === 'PENDING' ? (
                                  <span
                                    className="rental-sub-button"
                                    onClick={() => {
                                      setModalType(`RENTAL-CANCELLED`);
                                      setModalData({
                                        shop_order_id: state.shop_order_id,
                                        type: orders.type,

                                        rental_id: rental?.rental_id,
                                        rental_payment_info:
                                          rental?.rental_payment_info,
                                        rental_payment_id:
                                          rental?.rental_payment_info?.[
                                            rental?.rental_payment_info
                                              ?.length - 1
                                          ]?.rental_payment_id,
                                      });
                                    }}
                                  >
                                    구매취소
                                  </span>
                                ) : rental?.delivery_status === 'DELIVERED' ? (
                                  <span
                                    className="rental-sub-button"
                                    onClick={() => {
                                      setModalType(`RENTAL-REFUNDED`);
                                      setModalData({
                                        shop_order_id: state.shop_order_id,
                                        type: orders.type,

                                        rental_id: rental?.rental_id,
                                        rental_payment_info:
                                          rental?.rental_payment_info,
                                        rental_payment_id:
                                          rental?.rental_payment_info?.[
                                            rental?.rental_payment_info
                                              ?.length - 1
                                          ]?.rental_payment_id,
                                      });
                                    }}
                                  >
                                    계약해지
                                  </span>
                                ) : null}
                              </div>
                            )}
                          </div>

                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 2,
                            }}
                          >
                            {rental?.rental_start_date &&
                            rental?.rental_end_date ? (
                              <Text size={12}>
                                {Formatter.unixToDate(
                                  rental?.rental_start_date,
                                  'YYYY.MM.DD'
                                )}{' '}
                                ~{' '}
                                {Formatter.unixToDate(
                                  rental?.rental_end_date,
                                  'YYYY.MM.DD'
                                )}
                              </Text>
                            ) : (
                              <Text size={12}>
                                {Formatter.unixToDate(
                                  rental?.delivery_date,
                                  'YYYY.MM.DD'
                                )}{' '}
                                ~{' '}
                                {dayjs
                                  .unix(rental?.delivery_date)
                                  .add(rental?.contract_date, 'month')
                                  .format('YYYY.MM.DD')}
                              </Text>
                            )}
                            <Text size={12} color={'#666666'}>
                              {rental?.rental_payment_info?.length}회차 납부
                            </Text>
                          </div>

                          <span
                            onClick={
                              rental.delivery_status === 'DELIVERED'
                                ? () =>
                                    rental.delivery_status === 'DELIVERED' &&
                                    rental.review
                                      ? navigate('/mypage/review')
                                      : navigate(
                                          `/mypage/reviewform?orderNum=${encodeURIComponent(
                                            encrypt(rental.rental_id)
                                          )}&product=${encodeURIComponent(
                                            encrypt(rental.product.product_id)
                                          )}&type=rental`
                                        )
                                : null
                            }
                            style={{
                              fontSize: 14,
                              fontWeight: 400,
                              border: `1px solid ${
                                rental.delivery_status === 'DELIVERED'
                                  ? 'var(--black-color)'
                                  : '#ddd'
                              }`,
                              color:
                                rental.delivery_status === 'DELIVERED'
                                  ? 'var(--black-color)'
                                  : '#ddd',
                              borderRadius: 2,
                              padding: '10px 16px',
                              cursor:
                                rental.delivery_status === 'DELIVERED'
                                  ? 'pointer'
                                  : 'default',
                            }}
                          >
                            {rental.delivery_status === 'DELIVERED' &&
                            rental.review
                              ? '리뷰보기'
                              : '리뷰작성'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            },
          },
        ]}
        headerStyle={{
          display: 'none',
        }}
        headRowStyle={{
          border: 'none',
        }}
        bodyRowStyle={{ padding: 0 }}
        bordered={false}
        hovered={false}
        minHeight={orders.length ? 'auto' : 200}
        pagination={false}
      />

      {/* 취소/환불 사유 모달 */}
      <ModalLayout
        open={!!modalType}
        onCancel={() => setModalType(null)}
        width={350}
        centered
        styles={{
          content: {
            borderRadius: 8,
          },
        }}
        title={`주문 ${
          modalType?.includes('CANCELLED') ? '취소' : '환불'
        } 사유`}
      >
        <Content maxWidth={'100%'} padding={0}>
          <Row>
            <Col x={24}>
              <Content maxWidth={'100%'} padding={0}>
                <Select
                  size="large"
                  value={selectedValue}
                  placeholder={`${
                    modalType?.includes('CANCELLED') ? '취소' : '환불'
                  }를 선택해주세요`}
                  style={{ width: '100%' }}
                  options={REFUND_REASON[modalType?.split('-')[1]]?.map(
                    (reason) => ({
                      label: (
                        <div>
                          <Text size={14} weight={600}>
                            {reason.label}
                          </Text>
                          <Text size={12} color={'#BDBDBD'}>
                            {reason.value}
                          </Text>
                        </div>
                      ),
                      value: reason.value,
                    })
                  )}
                  onChange={(e) => setSelectedValue(e)}
                />
                {selectedValue !== null ? null : (
                  <Input.TextArea
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    style={{ marginTop: 8 }}
                  />
                )}
              </Content>
            </Col>
            <Col x={24}>
              <Content maxWidth={'100%'} padding={0} style={{ marginTop: 18 }}>
                <Button
                  style={{ width: '100%' }}
                  onClick={async () => {
                    const res = await onSubmit(modalType, {
                      ...modalData,
                      refund_reason: selectedValue ? selectedValue : inputValue,
                    });

                    if (res === 200) {
                      setModalData(null);
                      setInputValue('');
                      setSelectedValue('');
                      setModalType(null);
                    }
                  }}
                >
                  상품 {modalType?.includes('CANCELLED') ? '취소' : '환불'}
                </Button>
              </Content>
            </Col>
          </Row>
        </Content>
      </ModalLayout>
    </React.Fragment>
  );
};

export default RentalListTable;
